import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { userRequest } from "../api/requestMethods";
import {
  allGroupsSatisfied,
  hasPharmacyOptions,
  pharmacyLineOptions,
  pharmacyOptionGroupsLoaded,
  pharmacyOptionGroupsOf,
  selectionsFromLineOptions,
  toggleOptionInGroup,
} from "../utils/pharmacyOptions";

/**
 * usePharmacyOptions: one pharmacy product's option groups and the shopper's
 * picks (utils/pharmacyOptions holds the rules). Same contract as the apps'
 * hook of the same name.
 *
 * The storefront hands the dialog its groups as bare ids, so they are read
 * once from GET /healthcare/healthcare-product/:id. Until they load, or when
 * the read fails, adding is held (`blocksAdd`). `cartOptions`, the picks a
 * cart line already holds, become the starting picks once the groups load.
 */
export default function usePharmacyOptions(product, cartOptions = null) {
  const productId = product?._id ? String(product._id) : "";
  const declared = hasPharmacyOptions(product);
  const loadedWithProduct = pharmacyOptionGroupsLoaded(product);
  const [groups, setGroups] = useState(() => (loadedWithProduct ? pharmacyOptionGroupsOf(product) : []));
  const [status, setStatus] = useState(!declared || loadedWithProduct ? "ready" : "loading");
  const [attempt, setAttempt] = useState(0);
  const [selections, setSelections] = useState({});

  useEffect(() => {
    if (!declared || loadedWithProduct || !productId) return undefined;
    let cancelled = false;
    setStatus("loading");
    userRequest
      .get(`/healthcare/healthcare-product/${productId}`)
      .then((res) => {
        if (cancelled) return;
        setGroups(pharmacyOptionGroupsOf(res?.data));
        setStatus("ready");
      })
      .catch(() => {
        if (!cancelled) setStatus("failed");
      });
    return () => {
      cancelled = true;
    };
  }, [declared, loadedWithProduct, productId, attempt]);

  // The cart line's picks, once, when the groups are ready.
  const seededRef = useRef(false);
  useEffect(() => {
    if (seededRef.current || status !== "ready" || !groups.length) return;
    seededRef.current = true;
    if (Array.isArray(cartOptions) && cartOptions.length) {
      setSelections(selectionsFromLineOptions(groups, cartOptions));
    }
  }, [status, groups, cartOptions]);

  const toggle = useCallback((group, option) => {
    setSelections((prev) => ({
      ...prev,
      [group._id]: toggleOptionInGroup(prev[group._id] || [], option, group),
    }));
  }, []);

  const retry = useCallback(() => setAttempt((n) => n + 1), []);

  // Groups that turn out to hold nothing pickable ask for nothing.
  const active = declared && (status !== "ready" || groups.length > 0);
  const satisfied = status === "ready" && allGroupsSatisfied(groups, selections);
  const lineOptions = useMemo(() => pharmacyLineOptions(groups, selections), [groups, selections]);

  return { active, status, groups, selections, toggle, retry, blocksAdd: active && !satisfied, lineOptions };
}
